import { useState } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { ChevronRight, ExternalLink, X } from 'lucide-react';
import { EXTERNAL_NAV_ITEMS, INTERNAL_NAV_GROUPS, isNavItemActive } from '@/lib/admin-navigation';
import { useSidebar } from './sidebar-context';

const SHORTCUTS_KEY = 'logink-admin-shortcuts-open';

export function Sidebar() {
  const { open, setOpen } = useSidebar();
  const pathname = useRouterState({ select: state => state.location.pathname });
  const tab = useRouterState({
    select: state => {
      const raw = (state.location.search as { tab?: unknown }).tab;
      return typeof raw === 'string' ? raw : undefined;
    },
  });

  const [shortcutsOpen, setShortcutsOpen] = useState(() => {
    try { return localStorage.getItem(SHORTCUTS_KEY) === '1'; } catch { return false; }
  });
  function toggleShortcuts() {
    setShortcutsOpen(prev => {
      const next = !prev;
      try { localStorage.setItem(SHORTCUTS_KEY, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  }

  return (
    <>
      {/* Mobile backdrop */}
      <div
        aria-hidden
        onClick={() => setOpen(false)}
        className={[
          'md:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity',
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
        ].join(' ')}
      />

      <aside
        aria-label="사이드바"
        className={[
          'fixed md:sticky md:top-0 inset-y-0 left-0 z-50',
          'w-[248px] shrink-0',
          'bg-muted/30 border-r border-border/80',
          'flex flex-col h-screen',
          'transition-transform duration-200 ease-out',
          open ? 'translate-x-0' : '-translate-x-full',
          'md:translate-x-0',
        ].join(' ')}
      >
        <div className="h-14 px-4 flex items-center justify-between border-b border-border/60">
          <Link to="/" onClick={() => setOpen(false)} className="flex items-center gap-2.5">
            <span
              aria-hidden
              className="h-6.5 w-6.5 rounded-md bg-primary text-primary-foreground grid place-items-center text-[11px] font-bold tracking-tight shadow-xs"
            >
              L
            </span>
            <span className="text-[15px] font-semibold tracking-tight">Logink</span>
            <span className="text-[10.5px] font-mono text-muted-foreground/70 tabular-nums">admin</span>
          </Link>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="사이드바 닫기"
            className="md:hidden -mr-1 h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-2.5 py-3 space-y-4">
          {INTERNAL_NAV_GROUPS.map(group => (
            <div key={group.title || 'top'}>
              {group.title && (
                <div className="px-2.5 mb-1 text-[10px] font-bold uppercase tracking-[0.12em] text-primary/80">
                  {group.title}
                </div>
              )}
              <ul className="space-y-0.5">
                {group.items.map(item => {
                  const { to, label, icon: Icon } = item;
                  const active = isNavItemActive(item, pathname, tab);
                  return (
                    <li key={`${to}:${item.search?.tab ?? ''}`}>
                      <Link
                        to={to}
                        search={item.search}
                        onClick={() => setOpen(false)}
                        aria-current={active ? 'page' : undefined}
                        className={[
                          'group relative flex h-8 items-center gap-2.5 px-2.5 rounded-md text-[13px] transition-colors',
                          'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
                          active
                            ? "bg-accent text-foreground font-semibold before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-4 before:w-[3px] before:rounded-full before:bg-primary before:content-['']"
                            : 'text-muted-foreground hover:bg-accent/80 hover:text-foreground',
                        ].join(' ')}
                      >
                        <Icon className={`h-4 w-4 shrink-0 group-hover:opacity-100 ${active ? '' : 'opacity-70'}`} strokeWidth={1.75} />
                        <span className="tracking-tight">{label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          <div>
            <button
              type="button"
              onClick={toggleShortcuts}
              aria-expanded={shortcutsOpen}
              className="w-full px-2.5 mb-1 flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground/65 hover:text-foreground transition-colors"
            >
              <ChevronRight
                className={['h-3 w-3 transition-transform', shortcutsOpen ? 'rotate-90' : ''].join(' ')}
                strokeWidth={2}
              />
              바로가기
            </button>
            {shortcutsOpen && (
              <ul className="space-y-0.5">
                {EXTERNAL_NAV_ITEMS.map(({ href, label, icon: Icon }) => (
                  <li key={href}>
                    <a
                      href={href}
                      target="_blank"
                      rel="noreferrer noopener"
                      onClick={() => setOpen(false)}
                      className="group relative flex h-8 items-center gap-2.5 px-2.5 rounded-md text-[13px] text-muted-foreground hover:bg-accent/80 hover:text-foreground transition-colors"
                    >
                      <Icon className="h-4 w-4 shrink-0 opacity-70 group-hover:opacity-100" strokeWidth={1.75} />
                      <span className="tracking-tight flex-1">{label}</span>
                      <ExternalLink className="h-3 w-3 shrink-0 opacity-40 group-hover:opacity-70" strokeWidth={1.75} />
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </nav>

        <div className="px-4 py-3 border-t border-border/70 bg-background/25">
          <div className="text-[11px] text-muted-foreground/80 flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-signal" aria-hidden />
            <span className="font-mono tabular-nums">v1.0</span>
            <span className="text-muted-foreground/50">·</span>
            <span>admin.zerry.co.kr</span>
          </div>
        </div>
      </aside>
    </>
  );
}
